import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAdminI18n } from '../../../lib/i18n/use-admin-i18n';
import { useAuthStore } from '../../../stores/authStore';
import { saasApi } from '../../../lib/api/saas.api';
import { withAreaPrefix } from '../../../lib/area-routes';

// ─── Stats: dashboard counts from dedicated API ─────────────────────────────

function useDashboardStats() {
  const tenantId = useAuthStore(s => s.tenantId ?? null);

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-stats', tenantId ?? ''],
    queryFn: () => saasApi.getDashboardStats(tenantId ?? undefined),
    staleTime: 60_000,
  });

  return {
    tenants: data?.tenantsCount ?? null,
    tenantsLoading: isLoading,
    plans: data?.plansCount ?? null,
    plansLoading: isLoading,
    products: data?.productsCount ?? null,
    productsLoading: isLoading,
    subscriptions: data?.subscriptionsCount ?? null,
    subscriptionsLoading: isLoading,
    anyLoading: isLoading,
  };
}

// ─── Stat card (4 KPI cards) ───────────────────────────────────────────────

const ACCENT_MAP = {
  primary: {
    cardBg: 'bg-primary/5 border-primary/10',
    icon: 'bg-primary/15 text-primary ring-primary/20',
    bar: 'bg-primary',
    link: 'text-primary hover:text-primary/80',
  },
  success: {
    cardBg: 'bg-[hsl(var(--success))]/10 border-[hsl(var(--success))]/20',
    icon: 'bg-[hsl(var(--success))]/20 text-[hsl(var(--success))] ring-[hsl(var(--success))]/30',
    bar: 'bg-[hsl(var(--success))]',
    link: 'text-[hsl(var(--success))] hover:opacity-90',
  },
  warning: {
    cardBg: 'bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]/20',
    icon: 'bg-[hsl(var(--warning))]/20 text-[hsl(var(--warning))] ring-[hsl(var(--warning))]/30',
    bar: 'bg-[hsl(var(--warning))]',
    link: 'text-[hsl(var(--warning))] hover:opacity-90',
  },
  info: {
    cardBg: 'bg-[hsl(var(--info))]/10 border-[hsl(var(--info))]/20',
    icon: 'bg-[hsl(var(--info))]/20 text-[hsl(var(--info))] ring-[hsl(var(--info))]/30',
    bar: 'bg-[hsl(var(--info))]',
    link: 'text-[hsl(var(--info))] hover:opacity-90',
  },
} as const;

interface StatCardProps {
  readonly title: string;
  readonly value: number | null;
  readonly loading?: boolean;
  readonly href?: string;
  readonly icon: string;
  readonly viewLinkLabel: string;
  readonly accent?: 'primary' | 'success' | 'warning' | 'info';
}

function StatCard(props: Readonly<StatCardProps>) {
  const { title, value, loading, href, icon, viewLinkLabel, accent = 'primary' } = props;
  const styles = ACCENT_MAP[accent] ?? ACCENT_MAP.primary;

  const displayValue = loading ? null : value;

  const content = (
    <>
      {/* Accent bar on left */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${styles.bar}`} />
      <div className="relative flex items-start justify-between gap-4 pl-1">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {title}
          </p>
          <p className="mt-2 text-3xl font-bold tabular-nums tracking-tight text-foreground md:text-4xl">
            {loading && (
              <span className="inline-block h-9 w-14 animate-pulse rounded bg-muted" />
            )}
            {!loading && displayValue !== null && displayValue}
            {!loading && displayValue === null && (
              <span className="text-muted-foreground">—</span>
            )}
          </p>
          {href && (
            <span className={`mt-3 inline-flex text-sm font-medium ${styles.link}`}>
              {viewLinkLabel} →
            </span>
          )}
        </div>
        <span
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-2xl ring-2 ring-inset ${styles.icon}`}
          aria-hidden
        >
          {icon}
        </span>
      </div>
    </>
  );

  const baseClass =
    `relative overflow-hidden rounded-xl border p-5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2 focus:ring-offset-background ${styles.cardBg}`;

  if (href) {
    return (
      <Link
        to={href}
        className={`block ${baseClass} hover:shadow-lg hover:opacity-95`}
      >
        {content}
      </Link>
    );
  }

  return <div className={baseClass}>{content}</div>;
}

// ─── Shortcut tile ────────────────────────────────────────────────────────

interface ShortcutTileProps {
  readonly title: string;
  readonly description: string;
  readonly path: string;
  readonly icon: string;
}

function ShortcutTile(props: Readonly<ShortcutTileProps>) {
  const { title, description, path, icon } = props;
  return (
    <Link
      to={path}
      className="group flex items-start gap-4 rounded-xl border border-border bg-card p-5 transition-all duration-200 hover:border-primary/30 hover:bg-muted/30 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2 focus:ring-offset-background"
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary text-xl transition-colors group-hover:bg-primary/20">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <span className="font-semibold text-foreground group-hover:text-primary transition-colors">
          {title}
        </span>
        <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
      </div>
      <span className="shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" aria-hidden>
        →
      </span>
    </Link>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────

const SHORTCUT_DEFS = [
  { id: 'products', path: '/products', scope: 'product:read', icon: '📦' },
  { id: 'tenants', path: '/tenants', scope: 'tenant:manage', icon: '🏛️' },
  { id: 'plans', path: '/plans', scope: 'plan:read', icon: '💳' },
  { id: 'subscriptions', path: '/subscriptions', scope: 'subscription:read', icon: '📋' },
  { id: 'settings', path: '/settings/users', scope: 'user:read', icon: '⚙️' },
] as const;

export function SaasDashboardPage() {
  const { page, language } = useAdminI18n();
  const hasScope = useAuthStore(s => s.hasScope);
  const user = useAuthStore(s => s.user);
  const stats = useDashboardStats();

  const displayName = user?.name?.trim() || user?.email?.split('@')[0] || 'Admin';
  const viewLinkLabel = page('dashboard', 'view_link');

  const visibleShortcuts = useMemo(
    () =>
      SHORTCUT_DEFS.filter(item => !item.scope || hasScope(item.scope)).map(item => ({
        ...item,
        title: page('dashboard', `shortcuts.${item.id}.title`),
        description: page('dashboard', `shortcuts.${item.id}.description`),
      })),
    [hasScope, page, language],
  );

  return (
    <div className="min-h-full space-y-6 p-4 md:p-6">
      {/* Welcome */}
      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">
          {page('dashboard', 'welcome', { name: displayName })}
        </h1>
        <p className="text-muted-foreground text-sm md:text-base">{page('dashboard', 'subtitle')}</p>
      </header>

      {/* KPI row — 4 stat cards */}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title={page('dashboard', 'stats.tenants')}
          value={stats.tenants}
          loading={stats.tenantsLoading}
          href={hasScope('tenant:manage') ? withAreaPrefix('/tenants', globalThis.location.pathname) : undefined}
          icon="🏛️"
          accent="primary"
          viewLinkLabel={viewLinkLabel}
        />
        <StatCard
          title={page('dashboard', 'stats.products')}
          value={stats.products}
          loading={stats.productsLoading}
          href={hasScope('product:read') ? withAreaPrefix('/products', globalThis.location.pathname) : undefined}
          icon="📦"
          accent="info"
          viewLinkLabel={viewLinkLabel}
        />
        <StatCard
          title={page('dashboard', 'stats.plans')}
          value={stats.plans}
          loading={stats.plansLoading}
          href={hasScope('plan:read') ? withAreaPrefix('/plans', globalThis.location.pathname) : undefined}
          icon="💳"
          accent="success"
          viewLinkLabel={viewLinkLabel}
        />
        <StatCard
          title={page('dashboard', 'stats.subscriptions')}
          value={stats.subscriptions}
          loading={stats.subscriptionsLoading}
          href={
            hasScope('subscription:read')
              ? withAreaPrefix('/subscriptions', globalThis.location.pathname)
              : undefined
          }
          icon="📋"
          accent="warning"
          viewLinkLabel={viewLinkLabel}
        />
      </section>

      {/* Shortcuts */}
      <section>
        <h2 className="mb-4 text-lg font-semibold text-foreground">
          {page('dashboard', 'quick_access')}
        </h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {visibleShortcuts.map(t => (
            <ShortcutTile
              key={t.path}
              title={t.title}
              description={t.description}
              path={withAreaPrefix(t.path, globalThis.location.pathname)}
              icon={t.icon}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
